import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, ViewChild,
  ViewChildren, ElementRef, QueryList, OnChanges, HostListener,
} from '@angular/core'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import FuzzySearch from 'fuzzy-search'
import { Selection, SelectionService } from 'common/util/services/selection.service'
import { PickerItem, PickerOptions } from '../types'

interface PickerGroup<T> {
  name: string|null
  items: PickerItem<T>[]
}

@Component({
  selector: 'object-picker-modal',
  templateUrl: './objectPickerModal.component.pug',
  styleUrls: ['./objectPickerModal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectPickerModal<T> implements OnChanges {
  @Input() options: PickerOptions<T>
  @ViewChild('input') input: ElementRef<HTMLInputElement>
  @ViewChildren('listItem') listItems: QueryList<ElementRef<HTMLElement>>

  selection: Selection<PickerItem<T>>
  items: PickerItem<T>[] = []
  searchResults: PickerItem<T>[] = []
  focused: PickerItem<T>|null = null
  groups: Record<string, PickerGroup<T>|undefined> = {}
  groupKeys: string[] = []
  search = new FuzzySearch<PickerItem<T>>([])
  filter = ''
  loading = false
  shiftPressed = false

  trackByIndex = i => i

  constructor (
    selection: SelectionService,
    private modalInstance: NgbActiveModal,
    private cdr: ChangeDetectorRef,
  ) {
    this.selection = selection.make()
  }

  @HostListener('keydown', ['$event'])
  handleKeyDown (event: KeyboardEvent) {
    const groupSorted = this.groupKeys.flatMap(key => {
      return (this.groups[key] ?? { items: [] }).items
    })
    const focuseable = groupSorted.filter(item => {
      return this.searchResults.includes(item) || item === this.focused
    })
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault()
      event.stopPropagation()

      let index = event.key === 'ArrowDown' ? 0 : this.listItems.length - 1

      if (this.focused) {
        const diff = event.key === 'ArrowDown' ? 1 : -1
        index = focuseable.map(item => item.id).indexOf(this.focused.id) + diff
        index = Math.min(Math.max(index, 0), this.listItems.length - 1)
      }

      this.listItems.get(index)?.nativeElement.focus()
    }
  }

  focusChange (item) {
    this.focused = item
  }

  async ngOnInit () {
    this.loading = true
    if ('then' in this.options.items) {
      this.items = await this.options.items
    } else {
      this.items = this.options.items
    }
    this.loading = false
    let id = 0
    this.items.forEach(item => {
      item.id = id++
    })

    this.search = new FuzzySearch(this.items, this.options.searchFields ?? ['name', 'group'], { sort: true })
    this.selection.setObjects(this.items)

    for (const item of this.items) {
      if (item.selected) {
        this.selection.select(item)
      }
    }

    this.updateFilter()
    this.cdr.detectChanges() // in case `items` was a promise
  }

  ngOnChanges (changes): void {
    if (changes['items']) {
      let id = 0
      this.items.forEach(item => {
        item.id = id++
      })
    }
  }

  updateFilter () {
    this.focused = null
    if (this.filter) {
      this.searchResults = this.search.search(this.filter)
    } else {
      this.searchResults = this.items.slice()
    }

    this.groups = {}
    for (const item of this.searchResults) {
      const groupName = item.group ?? ''
      if (!this.groups[groupName]) {
        this.groups[groupName] = {
          name: groupName,
          items: [],
        }
      }
      this.groups[groupName]!.items.push(item)
    }

    this.groupKeys = [
      ...this.options.groupOrder ?? [],
      ...Object.keys(this.groups).filter(x => !this.options.groupOrder?.includes(x)),
    ].filter(k => this.groups[k]?.items.length)
  }

  getTheOnlyOneVisible () {
    if (this.searchResults.length === 1) {
      return this.searchResults[0]
    }
    return null
  }

  handleEnter () {
    if (this.options.multiple && !this.selection.isEmpty()) {
      this.select(this.selection.get())
    }
    const item = this.getTheOnlyOneVisible()
    if (item) {
      this.select([item])
    }
  }

  select (items) {
    this.modalInstance.close(items.map(x => x.object))
  }

  selectAllClick () {
    this.selection.selectAll()
  }

  selectNoneClick () {
    this.selection.clear()
  }
}
